import {defaultKeymap, history, historyKeymap} from '@codemirror/commands'
import {type Extension, StateEffect} from '@codemirror/state'
import {EditorView, keymap, placeholder, type ViewUpdate} from '@codemirror/view'
import {readOnly} from '@github-ui/code-mirror/extensions/readonly'
import {spacingControls} from '@github-ui/code-mirror/extensions/spacing-controls'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {decorateMarkdownPlugin} from './codemirror/extensions/decorate-markdown'
import {singleLineEditorPlugin} from './codemirror/extensions/single-line-editor'
import styles from './HybridMarkdownEditor.module.css'
import {type MarkdownViewMode, ViewSwitch} from './ViewSwitch'

export interface HybridMarkdownEditorProps {
  /** This is only the initial value. Changes to this value after mount are ignored. */
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  onChangeViewMode?: (newViewMode: MarkdownViewMode) => void
  placeholder?: string
}

export function HybridMarkdownEditor({
  value,
  onChange,
  disabled,
  onChangeViewMode,
  placeholder: placeholderText,
}: HybridMarkdownEditorProps) {
  const internalOnChange = useCallback(
    (update: ViewUpdate) => {
      if (update.docChanged) onChange(update.state.doc.toString())
    },
    [onChange],
  )

  const extensions = useMemo<Extension[]>(() => {
    const result = [
      singleLineEditorPlugin,
      decorateMarkdownPlugin,
      history(),
      EditorView.updateListener.of(internalOnChange),
      EditorView.contentAttributes.of({
        'aria-label': 'Markdown content',
        class: clsx(styles.content, 'markdown-body'),
      }),
      spacingControls({
        indentUnit: 2,
        indentWithTabs: false,
        lineWrapping: true,
      }),
      // must go *after* singleLineEditorPlugin
      keymap.of([...defaultKeymap, ...historyKeymap]),
    ]

    if (placeholderText) result.push(placeholder(placeholderText))
    if (disabled) result.push(readOnly())

    return result
  }, [placeholderText, disabled, internalOnChange])

  const containerRef = useRef<HTMLDivElement>(null)
  const [view, setView] = useState<EditorView | null>(null)

  const initialValueRef = useRef(value)
  const initialExtensionsRef = useRef(extensions)
  useEffect(function initializeEditor() {
    const parent = containerRef.current
    if (!parent) return

    const editorView = new EditorView({
      parent,
      doc: initialValueRef.current,
      extensions: initialExtensionsRef.current,
    })

    setView(editorView)

    return () => {
      editorView.destroy()
      setView(null)
    }
  }, [])

  // TODO: We can probably do a better job of updating individual extensions rather than replacing the entire
  // set whenever a prop changes
  useEffect(
    function updateExtensions() {
      view?.dispatch({
        effects: StateEffect.reconfigure.of(extensions),
      })
    },
    [extensions, view],
  )

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.viewSwitch}>
          <ViewSwitch selectedView="hybrid" onViewSelect={onChangeViewMode} />
        </div>
        <div className={styles.formattingTools} />
      </div>

      <div ref={containerRef} />
    </div>
  )
}

try{ HybridMarkdownEditor.displayName ||= 'HybridMarkdownEditor' } catch {}